import React, { useState } from "react";
import { useLayoutEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { PlayCircle, BookOpen, GraduationCap } from "lucide-react";

const KnowledgeCenter = () => {
  const [activeTab, setActiveTab] = useState("sas");
useLayoutEffect(() => { window.scrollTo(0, 0); }, []);
  /* =======================
          SAS VIDEOS  
  ======================= */
  const sasVideos = [
    {
      link: "/vedios/Base SAS 9.4 Programming - Part 1.mp4",
      title: "Base SAS 9.4 Programming - Part 1",
      summary:
        "Covers creating SAS data sets, investigating libraries, combining data, handling SAS dates, and controlling observations and variables for processing.",
    },
    {
      link: "/vedios/Base SAS 9.4 Programming - Part 2.mp4",
      title: "Base SAS 9.4 Programming - Part 2",
      summary:
        "Covers sorting, conditional execution, data manipulation, DO loops, PROC TRANSPOSE, and macro variables for efficient SAS data processing.",
    },
    {
      link: "/vedios/Building Advanced Dashboards Using SAS.mp4",
      title: "Building Advanced Dashboards Using SAS",
      summary:
        "Covers building advanced dashboards with SAS Visual Analytics features like table joins, parameters, prompts, formatting, page hiding, and report tips.",
    },
    {
      link: "/vedios/Using Python for Data Analytics in SAS Viya.mp4",
      title: "Using Python for Data Analytics in SAS Viya",
      summary:
        "Covers using SWAT package to integrate Python with SAS Viya's CAS engine for analytics and distributed processing.",
    },
    {
      link: "/vedios/End-to-End Machine Learning in Model Studio.mp4",
      title: "End-to-End Machine Learning in Model Studio",
      summary:
        "Covers SAS analytics life cycle including data prep, ML pipelines, interpretability, and deployment.",
    },
    {
      link: "/vedios/SAS Viya Quick Start Tutorials.mp4",
      title: "SAS Viya Quick Start Tutorials",
      summary:
        "Overview of SAS Viya tools covering analytics lifecycle and ML workflow.",
    },
  ];

  /* =======================
          AI VIDEOS  
  ======================= */
  const aiVideos = [
    {
      link: "/vedios/Transformers Explained Visually  DL5.mp4",
      title: "Transformers Explained Visually | DL5",
      summary:
        "Visual explanation of transformers and how LLMs generate text.",
    },
    {
      link: "/vedios/AI Python for Beginners  DeepLearning.AI.mp4",
      title: "AI Python for Beginners | DeepLearning.AI",
      summary: "Course by Andrew Ng introducing Python and AI concepts.",
    },
    {
      link: "/vedios/Harvard CS50 – AI with Python.mp4",
      title: "Harvard CS50 – AI with Python",
      summary:
        "Foundational AI algorithms and hands-on Python AI learning.",
    },
    {
      link: "/vedios/AI in Petroleum Industry.mp4",
      title: "AI in Petroleum Industry",
      summary:
        "Real-world use cases of AI transforming petroleum operations.",
    },
    {
      link: "/vedios/AI in Petroleum Engineering.mp4",
      title: "AI in Petroleum Engineering",
      summary:
        "Detailed examples of AI applications in petroleum engineering.",
    },
    {
      link: "/vedios/What is a Neural Network.mp4",
      title: "What is a Neural Network?",
      summary:
        "Explains neural networks and their learning process visually.",
    },
  ];

  /* =======================
        RENDER VIDEO CARDS
  ======================= */
  const renderVideos = (title, subtitle, videosList) => (
    <div className="mt-16">
      <h2 className="text-3xl font-bold mb-2 text-gradient">{title}</h2>
      <p className="text-gray-300 mb-6">{subtitle}</p>

      <div className="grid md:grid-cols-2 gap-10">
        {videosList.map((v, index) => (
          <Card
            key={index}
            className="border border-green-700/30 bg-[#101010]/90 hover:border-red-600/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,0,0,0.3)]"
          >
                  <div className="p-5 bg-black/60 backdrop-blur-sm">
                <h3 className="text-lg font-bold text-white drop-shadow-md">
                  {v.title}
                </h3>
                <p className="text-gray-300 text-sm mt-1">{v.summary}</p>
              </div>
            <CardContent className="p-0 overflow-hidden">
              <video
                src={v.link}
                width="100%"
                height="315"
                controls
                playsInline
                className="rounded-t-lg bg-black"
                style={{ height: "315px", objectFit: "contain" }}
              />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  /* =======================
              UI
  ======================= */
  return (
    <div className="min-h-screen pt-20 pb-16 px-6 bg-gradient-to-b from-black via-[#0a0a0a] to-[#111] text-white">
      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="text-center mb-16">


<p className="text-2xl font-bold  text-gray-300 max-w-3xl mx-auto text-center mt-6">
  Learning resources and <span className="text-red-400 font-semibold">training sessions</span> that helped teams 
  build, experiment, and deliver solutions.
</p>

        </div>
{/* TRAINING SESSION RECORDINGS */}
<div className="mt-6 mb-10 max-w-7xl mx-auto px-4">

  {/* Section Description */}
  <p className="text-center text-gray-400 text-sm mb-6">
    Recorded training sessions delivered by{" "}
    <span className="text-red-400">SAS experts</span> to support teams in building AI and analytics solutions.
  </p>

  {/* Multiple Videos Grid */}
  <div className="flex flex-wrap justify-center gap-6 mb-12">
    {[
      { 
        title: "Session 1", 
        desc: "Foundations of analytics", 
        src: "/vedios/GMT20260127-093641_Recording_2560x1600.mp4", 
        color: "text-red-400",
        isNew: true
      },
      { 
        title: "Session 2", 
        desc: "Advanced workflows", 
        src: "/vedios/GMT20260128-094532_Recording_gallery_2560x1600.mp4", 
        color: "text-green-400",
        isNew: true
      },
      { 
        title: "Session 3", 
        desc: "Foundations of analytics", 
        src: "/vedios/GMT20250528-093529_Recording_2426x1516 1.mp4", 
        color: "text-red-400"
      },
      { 
        title: "Session 4", 
        desc: "Advanced workflows", 
        src: "/vedios/GMT20250603-085811_Recording.cutfile.20250606060715224_1920x1080 1.mp4", 
        color: "text-green-400"
      },
    ].map((video, index) => (
      <Card
        key={index}
        className="relative border border-green-700/30 bg-[#101010]/90 hover:border-red-600/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,0,0,0.3)] w-full max-w-md"
      >

        {/* NEW Badge */}
        {video.isNew && (
          <div className="absolute top-3 right-3 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg animate-pulse z-10">
            NEW
          </div>
        )}

        <CardHeader className="p-4 bg-black/60 backdrop-blur-sm">
          <CardTitle className="text-white flex items-center gap-2 text-base md:text-lg">
            <PlayCircle className={`w-5 h-5 ${video.color}`} />
            SAS Expert Training – {video.title}
          </CardTitle>
          <CardDescription className="text-gray-300 text-sm mt-2">
            {video.desc}
          </CardDescription>
        </CardHeader>

        <CardContent className="p-0">
          <video
            src={video.src}
            autoPlay
            muted
            loop
            playsInline
            controls
            className="w-full h-56 md:h-64 rounded-b-lg object-cover bg-black"
          />
        </CardContent>
      </Card>
    ))}
  </div>

</div>





        {/* TABS */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab("sas")}
            className={`px-6 py-2 rounded-md font-semibold border ${
              activeTab === "sas"
                ? "bg-red-600 text-white border-red-700"
                : "bg-transparent border-gray-600 text-gray-300"
            }`}
          >
            SAS Training
          </button>

          <button
            onClick={() => setActiveTab("ai")}
            className={`px-6 py-2 rounded-md font-semibold border ${
              activeTab === "ai"
                ? "bg-green-600 text-white border-green-700"
                : "bg-transparent border-gray-600 text-gray-300"
            }`}
          >
            AI Training
          </button>
        </div>
{/* CONDITIONAL VIDEO SECTION */}
{activeTab === "sas"
  ? renderVideos(
      // Title with gradient red-green effect and bigger font
      <span className="text-4xl font-bold bg-gradient-to-r from-red-500 via-green-500 to-red-500 bg-clip-text text-transparent">
        SAS Training Assets - Videos , Articles
      </span>,
      // Description with larger text
      <span className="text-xl text-white">
        Explore training videos on SAS programming and AI tools, covering everything from fundamentals to advanced applications
      </span>,
      sasVideos
    )
  : renderVideos(
      // Title with gradient red-green effect and bigger font
      <span className="text-4xl font-bold bg-gradient-to-r from-red-500 via-green-500 to-red-500 bg-clip-text text-transparent">
        AI Training Assets - Videos , Articles
      </span>,
      // Description with larger text
      <span className="text-xl text-white">
        Learn AI with step-by-step tutorial videos, guiding you through its concepts, tools, and real-world use cases.
      </span>,
      aiVideos
    )}


      </div>
    </div>
  );
};

export default KnowledgeCenter;